// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import ViewField from '@react/components/elements/ViewField'
import { useOtChainState, useOtDispatch } from '@react/redux/store'
import { lytPoolExchangeRateSetTx } from '@react/signedTxs/lytPoolSetDynamicExchangeRate'
import { RoleEnum } from '@shared/Enums'
import { LytExchangeRateCheckCTO, LytExchangeRateCheckSchema } from '@shared/LytExchangeRateCheckCTO'
import { logOT } from '@shared/loggingShared'
import React, { useEffect, useState } from 'react'
import { fetchLytExchangeRateCheckCTO } from '@react/lyt/LytFetchers'

export default function LytExchangeRateCard(): React.ReactElement {
  const {
    stageConfiguration: { serverApiBaseUrl, restAPIKey, lytPoolAddr },
    userConnection: { roleEnum },
  } = useOtChainState()
  const [lytExchangeRateCheckCTO, setLytExchangeRateCheckCTO] = useState<LytExchangeRateCheckCTO>()
  const [_isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const dispatch = useOtDispatch()
  useEffect(() => {
    if (lytPoolAddr) {
      fetchLytExchangeRateCheckCTO(serverApiBaseUrl, restAPIKey, lytPoolAddr)
        .then((_lytExchangeRateCheckCTO) => {
          setLytExchangeRateCheckCTO(_lytExchangeRateCheckCTO)
        })
        .catch((error) => {
          logOT.error('Error fetching lyt exchange rate check cto:', error)
        })
    }
  }, [lytPoolAddr])

  const setExchangeRateAction = (): void => {
    if (lytPoolAddr && lytExchangeRateCheckCTO?.expectedExchangeRate) {
      setIsSubmitted(true)
      dispatch(
        lytPoolExchangeRateSetTx({
          poolAddr: lytPoolAddr,
          exchangeRate: lytExchangeRateCheckCTO?.expectedExchangeRate as string,
        })
      )
    }
  }
  if (!lytExchangeRateCheckCTO || (roleEnum !== RoleEnum.ISSUER && roleEnum !== RoleEnum.ISSUER_VAULT_WALLET)) {
    return <></>
  }
  return (
    <Paper
      sx={{
        padding: 2,

        backgroundColor: lytExchangeRateCheckCTO.isReconciled ? 'paper.default' : '#FFD580',
      }}
    >
      <Typography variant="h4">Exchange Rate Checker</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Box sx={{ display: 'flex', flex: 1 }}>
            <ViewField
              fieldSchema={LytExchangeRateCheckSchema.describe().fields.exchangeRate}
              liquidityTokenSymbol={'USDC'}
              poolTokenSymbol={'Sierra'}
              dataObject={lytExchangeRateCheckCTO}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 1 }}>
            <ViewField
              fieldSchema={LytExchangeRateCheckSchema.describe().fields.expectedExchangeRate}
              liquidityTokenSymbol={'USDC'}
              poolTokenSymbol={'Sierra'}
              dataObject={lytExchangeRateCheckCTO}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Box sx={{ display: 'flex', flex: 1 }}>
            <ViewField
              fieldSchema={LytExchangeRateCheckSchema.describe().fields.isReconciled}
              liquidityTokenSymbol={'USDC'}
              poolTokenSymbol={'Sierra'}
              dataObject={lytExchangeRateCheckCTO}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 1 }}>
            <ViewField
              fieldSchema={LytExchangeRateCheckSchema.describe().fields.assetDelta}
              liquidityTokenSymbol={'USDC'}
              poolTokenSymbol={'Sierra'}
              dataObject={lytExchangeRateCheckCTO}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Box sx={{ display: 'flex', flex: 1 }}>
            <ViewField
              fieldSchema={LytExchangeRateCheckSchema.describe().fields.totalAssets}
              liquidityTokenSymbol={'USDC'}
              poolTokenSymbol={'Sierra'}
              dataObject={lytExchangeRateCheckCTO}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 1 }}>
            <ViewField
              fieldSchema={LytExchangeRateCheckSchema.describe().fields.outstandingLytPoolShares}
              liquidityTokenSymbol={'USDC'}
              poolTokenSymbol={'Sierra'}
              dataObject={lytExchangeRateCheckCTO}
            />
          </Box>
        </Box>
      </Box>
      {roleEnum === RoleEnum.ISSUER && (
        <Button
          title={'Set Exchange Rate'}
          variant={'contained'}
          onClick={setExchangeRateAction}
          disabled={lytExchangeRateCheckCTO.isReconciled}
        >
          Set Exchange Rate
        </Button>
      )}
    </Paper>
  )
}
